import React, { useEffect, useState } from 'react';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import { Box, Dialog, Portal, Text, VStack } from '@chakra-ui/react';
import { SavedPaymentMethodResponse } from '@/client';
import SavedCardInfo from '@/components/payment/SavedCardInfo.tsx';
import { CloseButton } from '@/components/ui/close-button.tsx';
import {
	PAY_BUTTON_SHIMMER_CLASS,
	PAY_BUTTON_SHIMMER_CONTAINER_CSS,
	PAY_BUTTON_SHIMMER_CSS,
	PAY_BUTTON_SHIMMER_LAYER_PROPS,
} from '@/components/ui/motion.ts';

interface SavedCardPaymentConfirmationDialogProps {
	title?: string;
	/** Optional suffix shown after the amount, e.g. "per month" for a subscription. */
	amountSuffix?: string;
	isOpen: boolean;
	amountCents: number;
	card: SavedPaymentMethodResponse | null;
	isLoading?: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

/**
 * Requests explicit user confirmation before charging an existing saved card.
 */
const SavedCardPaymentConfirmationDialog: React.FC<SavedCardPaymentConfirmationDialogProps> = ({
	title = 'Confirm Bundle purchase?',
	amountSuffix,
	isOpen,
	amountCents,
	card,
	isLoading = false,
	onCancel,
	onConfirm,
}) => {
	const [displayAmountCents, setDisplayAmountCents] = useState(amountCents);
	const [displayCard, setDisplayCard] = useState(card);

	useEffect(() => {
		if (isOpen) {
			setDisplayAmountCents(amountCents);
			setDisplayCard(card);
		}
	}, [amountCents, card, isOpen]);

	const formattedAmount = new Intl.NumberFormat('en-NZ', {
		style: 'currency',
		currency: 'NZD',
	}).format(displayAmountCents / 100);

	return (
		<Dialog.Root
			size="lg"
			placement="center"
			motionPreset="slide-in-bottom"
			open={isOpen}
			onOpenChange={({ open }) => {
				if (!open) {
					onCancel();
				}
			}}
			closeOnInteractOutside={!isLoading}
			closeOnEscape={!isLoading}
		>
			<Portal>
				{!isLoading && <style>{PAY_BUTTON_SHIMMER_CSS}</style>}
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content maxW="440px" borderRadius="3xl" boxShadow="md">
						<Dialog.Header pb={2}>
							<Dialog.Title>{title}</Dialog.Title>
							<Dialog.CloseTrigger asChild position="absolute" top={4} right={4}>
								<CloseButton
									size="sm"
									disabled={isLoading}
									_disabled={{ cursor: 'default', opacity: 0.5 }}
								/>
							</Dialog.CloseTrigger>
						</Dialog.Header>

						<Dialog.Body p={6} pt={0}>
							<VStack align="start" gap={4}>
								<Text fontWeight="semibold" color="brand.fg">
									Amount: {formattedAmount}
									{amountSuffix ? ` ${amountSuffix}` : ''}
								</Text>
								{displayCard && <SavedCardInfo card={displayCard} />}
							</VStack>
						</Dialog.Body>

						<Dialog.Footer>
							<VStack gap={2} w="100%" align="stretch">
								<PrimaryButton
									onClick={onConfirm}
									loading={isLoading}
									loadingText="Processing payment..."
									disabled={isLoading}
									width="100%"
									css={isLoading ? undefined : PAY_BUTTON_SHIMMER_CONTAINER_CSS}
								>
									{!isLoading && (
										<Box
											aria-hidden="true"
											className={PAY_BUTTON_SHIMMER_CLASS}
											{...PAY_BUTTON_SHIMMER_LAYER_PROPS}
										/>
									)}
									<Box as="span" position="relative" zIndex={2}>
										Pay {formattedAmount} now
									</Box>
								</PrimaryButton>
								<Text textStyle="micro" lineHeight="short" color="fg.muted" textAlign="center" px={2}>
									By clicking Pay now, you allow Bundle to charge your card in the amount above.
								</Text>
							</VStack>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default SavedCardPaymentConfirmationDialog;
